/**
 * Platform DataGrid — reusable enterprise table (server-driven pages).
 * COMMERCE-WORKSPACE-000002: founder meta bar, skeleton, sticky filters,
 * saved views, density, keyboard shortcuts, a11y polish.
 * Domain-agnostic: Products, Inventory, Orders, Pricing, Finance wire columns + fetch.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export type DataGridColumn<T> = {
  id: string;
  header: string;
  width?: number;
  minWidth?: number;
  sortable?: boolean;
  hideable?: boolean;
  stickyLeft?: number;
  /** Column chooser grouping label (PRODUCTS-WORKSPACE reference) */
  group?: string;
  render: (row: T) => ReactNode;
  exportValue?: (row: T) => string;
};

export type DataGridMeta = {
  total_count: number;
  returned_count: number;
  page: number;
  page_size: number;
  pages: number;
  remaining: number;
  has_more: boolean;
  execution_time_ms?: number;
  mismatch_reason?: string;
  source?: string;
  last_sync?: string;
  data_source_badge?: string;
  /** Amazon-reported / last-pull count when known */
  amazon_count?: number;
  /** Realtime sync cue */
  realtime?: boolean;
};

export type PlatformDataGridProps<T> = {
  gridId: string;
  workspaceKey: string;
  userKey?: string;
  columns: DataGridColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  meta: DataGridMeta | null;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  emptyAction?: ReactNode;
  page: number;
  pageSize: PageSizeOption;
  sort?: string;
  order?: 'asc' | 'desc';
  searchDraft: string;
  searchApplied?: string;
  onSearchDraftChange: (v: string) => void;
  onSearch: () => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: PageSizeOption) => void;
  onSortChange?: (columnId: string, order: 'asc' | 'desc') => void;
  onRefresh?: () => void;
  toolbarExtra?: ReactNode;
  bulkActions?: ReactNode;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  bodyMaxHeight?: number;
  className?: string;
  /** compact = denser founder ops mode */
  density?: 'comfortable' | 'compact';
};

type Prefs = {
  pageSize: PageSizeOption;
  hidden: string[];
  widths: Record<string, number>;
  density?: 'comfortable' | 'compact';
  views?: Record<string, { hidden: string[]; pageSize: PageSizeOption }>;
};

function prefKey(gridId: string, workspaceKey: string, userKey?: string) {
  return `platform_datagrid:${workspaceKey || 'ws'}:${userKey || 'user'}:${gridId}`;
}

function loadPrefs(key: string, fallbackSize: PageSizeOption): Prefs {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { pageSize: fallbackSize, hidden: [], widths: {}, views: {} };
    const p = JSON.parse(raw) as Partial<Prefs>;
    const size = PAGE_SIZE_OPTIONS.includes(p.pageSize as PageSizeOption)
      ? (p.pageSize as PageSizeOption)
      : fallbackSize;
    return {
      pageSize: size,
      hidden: Array.isArray(p.hidden) ? p.hidden : [],
      widths: p.widths && typeof p.widths === 'object' ? p.widths : {},
      density: p.density === 'compact' ? 'compact' : 'comfortable',
      views: p.views && typeof p.views === 'object' ? p.views : {},
    };
  } catch {
    return { pageSize: fallbackSize, hidden: [], widths: {}, views: {} };
  }
}

function savePrefs(key: string, prefs: Prefs) {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    /* ignore quota */
  }
}

function escapeCsv(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtSync(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function PlatformDataGrid<T>(props: PlatformDataGridProps<T>) {
  const {
    gridId,
    workspaceKey,
    userKey,
    columns,
    rows,
    rowKey,
    meta,
    loading,
    error,
    emptyMessage = 'No rows.',
    emptyAction,
    page,
    pageSize,
    sort,
    order = 'asc',
    searchDraft,
    searchApplied,
    onSearchDraftChange,
    onSearch,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onRefresh,
    toolbarExtra,
    bulkActions,
    selectable,
    selectedKeys,
    onSelectionChange,
    bodyMaxHeight = 560,
    className = '',
    density: densityProp,
  } = props;

  const storageKey = prefKey(gridId, workspaceKey, userKey);
  const initial = loadPrefs(storageKey, 25);
  const [hidden, setHidden] = useState<string[]>(initial.hidden);
  const [widths, setWidths] = useState<Record<string, number>>(initial.widths);
  const [density, setDensity] = useState<'comfortable' | 'compact'>(
    densityProp ?? initial.density ?? 'comfortable',
  );
  const [views, setViews] = useState(initial.views ?? {});
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [hint, setHint] = useState('Press / to search · R refresh · C columns');
  const bodyRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const resizeCol = useRef<{ id: string; startX: number; startW: number } | null>(null);
  const rowH = density === 'compact' ? 32 : 40;

  useEffect(() => {
    const p = loadPrefs(storageKey, 25);
    if (p.pageSize !== pageSize) onPageSizeChange(p.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/workspace only
  }, [storageKey]);

  const persist = useCallback(
    (next: Partial<Prefs>) => {
      const cur = loadPrefs(storageKey, pageSize);
      const merged: Prefs = {
        pageSize: next.pageSize ?? cur.pageSize,
        hidden: next.hidden ?? cur.hidden,
        widths: next.widths ?? cur.widths,
        density: next.density ?? cur.density ?? density,
        views: next.views ?? cur.views ?? views,
      };
      savePrefs(storageKey, merged);
    },
    [storageKey, pageSize, density, views],
  );

  const visibleCols = useMemo(
    () => columns.filter((c) => !hidden.includes(c.id)),
    [columns, hidden],
  );

  const totalPages = meta?.pages ?? 1;
  const totalCount = meta?.total_count ?? 0;
  const returned = meta?.returned_count ?? rows.length;

  const useVirtual = rows.length > 40;
  const visibleCount = Math.ceil(bodyMaxHeight / rowH) + 6;
  const start = useVirtual ? Math.max(0, Math.floor(scrollTop / rowH) - 2) : 0;
  const end = useVirtual ? Math.min(rows.length, start + visibleCount) : rows.length;
  const slice = rows.slice(start, end);
  const padTop = useVirtual ? start * rowH : 0;
  const padBottom = useVirtual ? Math.max(0, (rows.length - end) * rowH) : 0;

  const allVisibleSelected =
    selectable && rows.length > 0 && rows.every((r) => selectedKeys?.has(rowKey(r)));

  const toggleAll = () => {
    if (!onSelectionChange || !selectedKeys) return;
    if (allVisibleSelected) {
      const next = new Set(selectedKeys);
      rows.forEach((r) => next.delete(rowKey(r)));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedKeys);
      rows.forEach((r) => next.add(rowKey(r)));
      onSelectionChange(next);
    }
  };

  const toggleOne = (key: string) => {
    if (!onSelectionChange || !selectedKeys) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  };

  const exportCsv = (excel: boolean) => {
    const cols = visibleCols;
    const header = cols.map((c) => escapeCsv(c.header)).join(',');
    const lines = rows.map((row) =>
      cols
        .map((c) => {
          const raw = c.exportValue
            ? c.exportValue(row)
            : String((row as Record<string, unknown>)[c.id] ?? '');
          return escapeCsv(raw);
        })
        .join(','),
    );
    const bom = excel ? '\uFEFF' : '';
    const body = bom + [header, ...lines].join('\r\n');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadBlob(
      `${gridId}-export-${stamp}.csv`,
      body,
      'text/csv;charset=utf-8',
    );
  };

  const onHeaderSort = (col: DataGridColumn<T>) => {
    if (!col.sortable || !onSortChange) return;
    if (sort === col.id) {
      onSortChange(col.id, order === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(col.id, 'asc');
    }
  };

  const onKeyNav = (e: KeyboardEvent) => {
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusIdx(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusIdx(rows.length - 1);
    } else if (e.key === ' ' && selectable) {
      e.preventDefault();
      const r = rows[focusIdx];
      if (r) toggleOne(rowKey(r));
    }
  };

  useEffect(() => {
    const onGlobal = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
        setHint('Search focused · Enter to apply');
      } else if ((e.key === 'r' || e.key === 'R') && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onRefresh?.();
        setHint('Refreshed');
      } else if ((e.key === 'c' || e.key === 'C') && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setColMenuOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onGlobal);
    return () => window.removeEventListener('keydown', onGlobal);
  }, [onRefresh]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeCol.current) return;
      const dx = e.clientX - resizeCol.current.startX;
      const id = resizeCol.current.id;
      const w = Math.max(60, resizeCol.current.startW + dx);
      setWidths((prev) => {
        const next = { ...prev, [id]: w };
        persist({ widths: next });
        return next;
      });
    };
    const onUp = () => {
      resizeCol.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [persist]);

  const colWidth = (c: DataGridColumn<T>) => widths[c.id] ?? c.width ?? 120;

  const saveView = () => {
    const name = viewName.trim();
    if (!name) return;
    const next = {
      ...views,
      [name]: { hidden: [...hidden], pageSize },
    };
    setViews(next);
    persist({ views: next });
    setViewName('');
    setViewMenuOpen(false);
    setHint(`View “${name}” saved`);
  };

  const applyView = (name: string) => {
    const v = views[name];
    if (!v) return;
    setHidden(v.hidden);
    onPageSizeChange(v.pageSize);
    persist({ hidden: v.hidden, pageSize: v.pageSize });
    setViewMenuOpen(false);
    setHint(`Applied view “${name}”`);
  };

  const btn =
    'inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-800 transition-colors duration-150 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800';
  const btnPrimary =
    'inline-flex min-h-11 items-center justify-center rounded-lg bg-slate-900 px-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:bg-teal-600 dark:hover:bg-teal-500';

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Sticky filter/toolbar shell */}
      <div className="sticky top-0 z-30 space-y-2 rounded-xl border border-slate-200/90 bg-white/95 p-3 shadow-sm backdrop-blur-sm dark:border-slate-700 dark:bg-slate-950/95">
        <div className="flex flex-wrap items-end gap-3">
          {toolbarExtra}
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Quick search</span>
            <div className="mt-1 flex gap-1">
              <input
                ref={searchRef}
                className="min-h-11 min-w-[14rem] rounded-lg border border-slate-300 px-3 font-mono text-xs dark:border-slate-600 dark:bg-slate-900"
                value={searchDraft}
                onChange={(e) => onSearchDraftChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
                placeholder="ASIN · SKU · Order · text…"
                aria-label="Search grid"
              />
              <button type="button" className={btnPrimary} onClick={onSearch}>
                Search
              </button>
            </div>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Rows</span>
            <select
              className="mt-1 block min-h-11 rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              value={pageSize}
              onChange={(e) => {
                const n = Number(e.target.value) as PageSizeOption;
                onPageSizeChange(n);
                persist({ pageSize: n });
              }}
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-slate-500">Density</span>
            <select
              className="mt-1 block min-h-11 rounded-lg border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              value={density}
              onChange={(e) => {
                const d = e.target.value === 'compact' ? 'compact' : 'comfortable';
                setDensity(d);
                persist({ density: d });
              }}
              aria-label="Table density"
            >
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </label>
          <div className="relative">
            <button type="button" className={btn} onClick={() => setColMenuOpen((o) => !o)} aria-expanded={colMenuOpen}>
              Columns
            </button>
            {colMenuOpen && (
              <div
                className="absolute z-40 mt-1 max-h-72 min-w-[15rem] overflow-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg dark:border-slate-600 dark:bg-slate-900"
                role="menu"
                aria-label="Column chooser"
              >
                {(() => {
                  const groups = new Map<string, typeof columns>();
                  for (const c of columns) {
                    const g = c.group || 'Columns';
                    const list = groups.get(g) ?? [];
                    list.push(c);
                    groups.set(g, list);
                  }
                  return Array.from(groups.entries()).map(([group, cols]) => (
                    <div key={group} className="mb-2 last:mb-0">
                      <p className="px-1 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                        {group}
                      </p>
                      {cols.map((c) => (
                        <label key={c.id} className="flex min-h-9 items-center gap-2 py-0.5 text-xs">
                          <input
                            type="checkbox"
                            checked={!hidden.includes(c.id)}
                            disabled={c.hideable === false}
                            onChange={() => {
                              setHidden((h) => {
                                const next = h.includes(c.id)
                                  ? h.filter((x) => x !== c.id)
                                  : [...h, c.id];
                                persist({ hidden: next });
                                return next;
                              });
                            }}
                          />
                          {c.header}
                        </label>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
          <div className="relative">
            <button type="button" className={btn} onClick={() => setViewMenuOpen((o) => !o)}>
              Saved views
            </button>
            {viewMenuOpen && (
              <div className="absolute z-40 mt-1 min-w-[16rem] space-y-2 rounded-lg border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-600 dark:bg-slate-900">
                <ul className="max-h-40 space-y-1 overflow-auto text-xs">
                  {Object.keys(views).length === 0 && (
                    <li className="text-slate-500">No saved views yet</li>
                  )}
                  {Object.keys(views).map((name) => (
                    <li key={name}>
                      <button type="button" className="w-full rounded px-2 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => applyView(name)}>
                        {name}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-1 border-t border-slate-100 pt-2 dark:border-slate-700">
                  <input
                    className="min-h-9 flex-1 rounded border border-slate-300 px-2 text-xs dark:border-slate-600 dark:bg-slate-950"
                    placeholder="View name"
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    aria-label="New saved view name"
                  />
                  <button type="button" className={btnPrimary + ' !min-h-9'} onClick={saveView}>
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
          <button type="button" className={btn} onClick={() => exportCsv(false)} disabled={rows.length === 0}>
            CSV
          </button>
          <button
            type="button"
            className={btn}
            onClick={() => exportCsv(true)}
            disabled={rows.length === 0}
            title="UTF-8 CSV opens in Excel"
          >
            Excel
          </button>
          {onRefresh && (
            <button type="button" className={btn} onClick={onRefresh} aria-label="Refresh data">
              Refresh
            </button>
          )}
        </div>
        {bulkActions && (
          <div
            className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t border-slate-200/80 pt-2 dark:border-slate-700"
            role="toolbar"
            aria-label="Bulk actions"
          >
            {bulkActions}
          </div>
        )}
        <p className="text-[11px] text-slate-500" aria-live="polite">
          {hint}
        </p>
      </div>

      {meta && (
        <div
          className="rounded-xl border border-teal-200/80 bg-gradient-to-r from-teal-50/90 to-slate-50 px-3 py-2.5 text-sm text-slate-900 dark:border-teal-900 dark:from-teal-950/40 dark:to-slate-900 dark:text-slate-100"
          role="status"
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm">
            <MetaChip label="Database" value={String(totalCount)} />
            <MetaChip label="Displayed" value={String(returned)} />
            <MetaChip
              label="Amazon count"
              value={meta.amazon_count != null ? String(meta.amazon_count) : '—'}
            />
            <MetaChip label="Page" value={`${meta.page} / ${Math.max(1, totalPages)}`} />
            <MetaChip
              label="API"
              value={meta.execution_time_ms != null ? `${meta.execution_time_ms} ms` : '—'}
            />
            <MetaChip label="Last sync" value={fmtSync(meta.last_sync)} mono />
            {meta.data_source_badge || meta.source ? (
              <MetaChip label="Source" value={meta.data_source_badge || meta.source || ''} mono />
            ) : null}
            {meta.realtime ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" aria-hidden />
                Realtime priority
              </span>
            ) : null}
          </div>
          <p className="mt-1.5 text-[11px] leading-snug text-slate-600 dark:text-slate-400">
            {meta.mismatch_reason ||
              (totalCount !== returned
                ? `Page slice: ${returned} of ${totalCount} (page_size=${meta.page_size}). Remaining after this page: ${meta.remaining}. Not data loss.`
                : 'All matching rows fit on this page.')}
            {searchApplied ? ` · Filter: “${searchApplied}”` : ''}
          </p>
        </div>
      )}

      {loading && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900" aria-busy="true" aria-label="Loading rows">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-8 animate-pulse rounded-md bg-slate-100 dark:bg-slate-800"
              style={{ animationDuration: '1.2s', animationDelay: `${i * 40}ms` }}
            />
          ))}
        </div>
      )}

      {error && (
        <div
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100"
          role="alert"
        >
          <p className="font-medium">Could not load this page</p>
          <p className="mt-1 text-xs opacity-90">{error}</p>
          {onRefresh ? (
            <button type="button" className={`${btn} mt-2 !border-red-300`} onClick={onRefresh}>
              Retry
            </button>
          ) : null}
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center dark:border-slate-600 dark:bg-slate-900">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-600 dark:text-slate-400">{emptyMessage}</p>
          {emptyAction ? <div className="mt-4 flex justify-center gap-2">{emptyAction}</div> : null}
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div
          className="overflow-x-auto rounded-xl border border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:border-slate-700"
          tabIndex={0}
          onKeyDown={onKeyNav}
          role="grid"
          aria-rowcount={rows.length}
          aria-label="Commerce data table"
        >
          <div className="min-w-max">
            <div className="sticky top-0 z-10 flex border-b border-slate-200 bg-slate-100/95 text-left text-xs font-semibold text-slate-600 backdrop-blur dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {selectable && (
                <div className="flex w-11 shrink-0 items-center justify-center border-r border-slate-200 px-1 py-2 dark:border-slate-700">
                  <input
                    type="checkbox"
                    checked={Boolean(allVisibleSelected)}
                    onChange={toggleAll}
                    aria-label="Select all on page"
                    className="h-4 w-4"
                  />
                </div>
              )}
              {visibleCols.map((c) => {
                const w = colWidth(c);
                const active = sort === c.id;
                const style: CSSProperties = {
                  width: w,
                  minWidth: c.minWidth ?? 60,
                  position: c.stickyLeft != null ? 'sticky' : undefined,
                  left: c.stickyLeft,
                  zIndex: c.stickyLeft != null ? 2 : undefined,
                  background: c.stickyLeft != null ? undefined : undefined,
                };
                return (
                  <div
                    key={c.id}
                    className={`relative shrink-0 select-none border-r border-slate-200 px-2 py-2.5 dark:border-slate-700 ${
                      c.stickyLeft != null ? 'bg-slate-100 dark:bg-slate-800' : ''
                    }`}
                    style={style}
                  >
                    <button
                      type="button"
                      className={`w-full text-left ${c.sortable ? 'cursor-pointer hover:text-slate-900 dark:hover:text-white' : 'cursor-default'}`}
                      onClick={() => onHeaderSort(c)}
                      disabled={!c.sortable}
                      aria-sort={
                        active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'
                      }
                    >
                      {c.header}
                      {active ? (order === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                    <span
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-teal-400/60"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        resizeCol.current = { id: c.id, startX: e.clientX, startW: w };
                      }}
                      aria-hidden
                    />
                  </div>
                );
              })}
            </div>
            <div
              ref={bodyRef}
              style={{ maxHeight: bodyMaxHeight, overflowY: 'auto' }}
              onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            >
              {padTop > 0 && <div style={{ height: padTop }} aria-hidden />}
              {slice.map((row, i) => {
                const idx = start + i;
                const key = rowKey(row);
                const focused = idx === focusIdx;
                return (
                  <div
                    key={key}
                    role="row"
                    className={`flex border-b border-slate-100 text-xs transition-colors duration-100 dark:border-slate-800 ${
                      focused
                        ? 'bg-teal-50 dark:bg-teal-950/40'
                        : 'bg-white hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900'
                    }`}
                    style={{ height: rowH, minHeight: 44 }}
                    onClick={() => setFocusIdx(idx)}
                  >
                    {selectable && (
                      <div className="flex w-11 shrink-0 items-center justify-center border-r border-slate-50 dark:border-slate-800">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedKeys?.has(key))}
                          onChange={() => toggleOne(key)}
                          aria-label={`Select ${key}`}
                          className="h-4 w-4"
                        />
                      </div>
                    )}
                    {visibleCols.map((c) => {
                      const w = colWidth(c);
                      const style: CSSProperties = {
                        width: w,
                        minWidth: c.minWidth ?? 60,
                        position: c.stickyLeft != null ? 'sticky' : undefined,
                        left: c.stickyLeft,
                        zIndex: c.stickyLeft != null ? 1 : undefined,
                      };
                      return (
                        <div
                          key={c.id}
                          className={`flex shrink-0 items-center overflow-hidden border-r border-slate-50 px-2 dark:border-slate-800 ${
                            c.stickyLeft != null
                              ? focused
                                ? 'bg-teal-50 dark:bg-teal-950/40'
                                : 'bg-white dark:bg-slate-950'
                              : ''
                          }`}
                          style={style}
                        >
                          {c.render(row)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              {padBottom > 0 && <div style={{ height: padBottom }} aria-hidden />}
            </div>
          </div>
        </div>
      )}

      {meta && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={page <= 1 || loading} className={btn} onClick={() => onPageChange(1)}>
            First
          </button>
          <button
            type="button"
            disabled={page <= 1 || loading}
            className={btn}
            onClick={() => onPageChange(Math.max(1, page - 1))}
          >
            Previous
          </button>
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Page {meta.page} / {Math.max(1, totalPages)}
          </span>
          <button
            type="button"
            disabled={!meta.has_more || loading}
            className={btn}
            onClick={() => onPageChange(page + 1)}
          >
            Next
          </button>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            className={btn}
            onClick={() => onPageChange(Math.max(1, totalPages))}
          >
            Last
          </button>
          {selectable && selectedKeys && selectedKeys.size > 0 && (
            <span className="text-xs font-medium text-slate-500">{selectedKeys.size} selected</span>
          )}
        </div>
      )}
    </div>
  );
}

function MetaChip({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <span className="inline-flex flex-col sm:flex-row sm:items-baseline sm:gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-teal-800/80 dark:text-teal-300/80">
        {label}
      </span>
      <span className={mono ? 'font-mono text-xs' : 'font-medium tabular-nums'}>{value}</span>
    </span>
  );
}
